import React, { useState, useRef, useEffect, useCallback } from "react";
import * as Tone from "tone";
import {
  Box,
  Typography,
  Paper,
  Button,
  ButtonBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useNoiseMonitor } from "./useNoiseMonitor";
import { useTranslation } from 'react-i18next';
import "../styles/hearingTest.css";
import headphoneConfig from '../config/headphoneConfig.json' with { type: 'json' };

const ABOVE_90_DBHL = 91;
const FREQUENCIES_ORDER = [1000, 2000, 4000, 6000, 8000, 500];

const isCypress = localStorage.getItem('isCypress') === 'true';

const PULSE_MS = isCypress ? 100 : 500;
const GAP_MIN_MS = isCypress ? 100 : 3000;
const GAP_MAX_MS = isCypress ? 200 : 5000;
const RESPONSE_WINDOW_MS = isCypress ? 500 : 3000;
const SPAM_INTERVAL = isCypress ? 100 : 1000;

export function convertDbHLtoDbFS(DbHL, freq) {
  // First try to get the value from the table.
  let DbFS = headphoneConfig?.[freq]?.[DbHL];
  if (!DbFS) {
    // As a backup, use the old method of conversion.
    const FREQUENCY_CONVERSION = {
      1000: 106.8,
      2000: 115,
      4000: 110,
      6000: 90,
      8000: 82,
      500: 108
    };
    DbFS = DbHL - FREQUENCY_CONVERSION[freq] ?? 0;
    console.log(`No dB FS value found in the config for ${DbHL} dB HL @${freq} Hz`);
  }
  return DbFS;
}

function HearingTest() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [started, setStarted] = useState(false);
  const [listening, setListening] = useState(false);
  const [paused, setPaused] = useState(false);
  const [spamDetected, setSpamDetected] = useState(false);

  const resultsRef = useRef({});
  const respondedThisTrialRef = useRef(false);
  const abortRef = useRef(false);
  const pauseRef = useRef(false);
  const developerRef = useRef(false);
  const clickTimestampsRef = useRef([]);

  const oscRef = useRef(null);
  const pannerRef = useRef(null);
  const volRef = useRef(null);

  const thresholdsDoneRef = useRef(0);

  const [noiseModalOpen, setNoiseModalOpen] = useState(false);
  const [noiseTryAgain, setNoiseTryAgain] = useState(false);
  const loudMsRef = useRef(0);
  const lastNoiseTickRef = useRef(null);

  const stopActiveToneRef = useRef(() => { });

  const { tooLoud, canResume, estimatedDb, error: noiseError } = useNoiseMonitor({
    enabled: started && !abortRef.current && !developerRef.current,
    splOffsetDb: 69.2,
    quietHoldMs: 1200,
    updateHz: 12,
  });

  useEffect(() => {
    if (!(started) || abortRef.current || developerRef.current) {
      loudMsRef.current = 0;
      lastNoiseTickRef.current = null;
      setNoiseModalOpen(false);
      setNoiseTryAgain(false);
      return;
    }

    const now = performance.now();
    const last = lastNoiseTickRef.current ?? now;
    const dt = now - last;
    lastNoiseTickRef.current = now;

    if (tooLoud) {
      loudMsRef.current += dt;

      if (loudMsRef.current >= 500 && !noiseModalOpen) {
        pauseRef.current = true;
        setListening(false);
        setNoiseModalOpen(true);
        setNoiseTryAgain(false);

        try { stopActiveToneRef.current?.(); } catch { }
      } else if (noiseModalOpen) {
        pauseRef.current = true;
      }
    } else {
      loudMsRef.current = 0;
    }
  }, [tooLoud, started, noiseModalOpen]);

  const handleNoiseResume = () => {
    if (!canResume) {
      setNoiseTryAgain(true);
      return;
    }
    setNoiseTryAgain(false);
    setNoiseModalOpen(false);
    pauseRef.current = false;
  };

  const sleepWithPause = useCallback(async (ms) => {
    const step = 100;
    let remaining = ms;
    while (remaining > 0) {
      if (abortRef.current || developerRef.current) return;
      if (pauseRef.current) {
        await new Promise((r) => setTimeout(r, step));
        continue;
      }
      const wait = Math.min(step, remaining);
      await new Promise((r) => setTimeout(r, wait));
      remaining -= wait;
    }
  }, []);

  const randGap = () => GAP_MIN_MS + Math.floor(Math.random() * (GAP_MAX_MS - GAP_MIN_MS));

  useEffect(() => {
    const panner = new Tone.Panner(0).toDestination();
    const osc = new Tone.Oscillator(1000, "sine");
    const vol = new Tone.Volume(-Infinity).connect(panner);
    osc.connect(vol);
    osc.start();
    pannerRef.current = panner;
    oscRef.current = osc;
    volRef.current = vol;

    stopActiveToneRef.current = () => {
      try { vol.volume.value = -Infinity; } catch { }
    };

    return () => {
      try {
        osc.stop();
        osc.disconnect();
        vol.disconnect();
        panner.disconnect();
      } catch { }
      stopActiveToneRef.current = () => { };
    };
  }, []);

  const tick = useCallback(async () => {
    await new Promise((r) => setTimeout(r, 0));
  }, []);

  const playPulseAndWaitForResponse = useCallback(
    async (freqHz, panValue, dBHL) => {
      try {
        await Tone.start();
      } catch { }

      await sleepWithPause(randGap());

      respondedThisTrialRef.current = false;
      setListening(true);

      const earNameForLog =
        panValue === -1 ? "Left" : panValue === 1 ? "Right" : `pan:${panValue}`;
      console.log(`Playing ${freqHz} Hz @ ${dBHL} dBHL (${earNameForLog})`);

      const osc = oscRef.current;
      const panner = pannerRef.current;
      const vol = volRef.current;
      if (!osc || !panner || !vol) {
        setListening(false);
        return false;
      }

      panner.pan.value = panValue;
      osc.frequency.value = freqHz;
      vol.volume.value = convertDbHLtoDbFS(dBHL, freqHz);
      await sleepWithPause(PULSE_MS);
      vol.volume.value = -Infinity;

      const pollStep = 50;
      let waited = 0;
      while (waited < RESPONSE_WINDOW_MS) {
        if (abortRef.current || developerRef.current) {
          setListening(false);
          return false;
        }
        if (pauseRef.current) {
          await new Promise((r) => setTimeout(r, pollStep));
          continue;
        }
        if (respondedThisTrialRef.current) {
          console.log(`Response detected for ${freqHz} Hz @ ${dBHL} dBHL (${earNameForLog})`);
          setListening(false);
          return true;
        }
        await new Promise((r) => setTimeout(r, pollStep));
        waited += pollStep;
      }
      setListening(false);
      return false;
    },
    [sleepWithPause]
  );

  const handleRespond = useCallback(() => {
    if (listening && !respondedThisTrialRef.current) {
      respondedThisTrialRef.current = true;
    }

    const now = Date.now();
    clickTimestampsRef.current.push(now);

    clickTimestampsRef.current = clickTimestampsRef.current.filter(
      (timestamp) => now - timestamp < SPAM_INTERVAL
    );

    if (clickTimestampsRef.current.length >= 3) {
      setSpamDetected(true);
    }
  }, [listening]);

  const pauseTest = useCallback(() => {
    abortRef.current = false;
    pauseRef.current = true;
    setPaused(true);
    setListening(false);
  }, []);

  const stopTest = useCallback(() => {
    abortRef.current = true;
    pauseRef.current = false;
    setListening(false);
    setPaused(false);

    setNoiseModalOpen(false);
    setNoiseTryAgain(false);
  }, []);

  const resumeTest = useCallback(() => {
    pauseRef.current = false;
    setPaused(false);
  }, []);

  const dismissSpamWarning = useCallback(() => {
    setSpamDetected(false);
    clickTimestampsRef.current = [];
  }, []);

  const findInitialThreshold = useCallback(
    async (freqHz, panValue) => {
      let currentDbHL = 30;
      while (!abortRef.current && !pauseRef.current) {
        const heard = await playPulseAndWaitForResponse(freqHz, panValue, currentDbHL);
        if (heard) {
          return currentDbHL;
        }
        if (currentDbHL === 90) {
          return ABOVE_90_DBHL;
        }
        currentDbHL += 20;
        await tick();
      }
      return null;
    },
    [playPulseAndWaitForResponse, tick]
  );

  const refineThreshold = useCallback(
    async (freqHz, panValue, initialDbHL) => {
      let currentDbHL = initialDbHL - 10;
      if (currentDbHL < -10) currentDbHL = -10;

      const heardCounts = { [initialDbHL]: 1 };
      let done = false;

      while (!abortRef.current && !pauseRef.current && !developerRef.current && !done) {
        const heard = await playPulseAndWaitForResponse(freqHz, panValue, currentDbHL);
        if (heard) {
          heardCounts[currentDbHL] = (heardCounts[currentDbHL] || 0) + 1;
          if (heardCounts[currentDbHL] >= 2) {
            return currentDbHL;
          }
          currentDbHL -= 10;
          if (currentDbHL < -10) currentDbHL = -10;
        } else {
          if (currentDbHL === 90) {
            return ABOVE_90_DBHL;
          }
          currentDbHL += 5;
        }
        await tick();
      }
      return null;
    },
    [playPulseAndWaitForResponse, tick]
  );

  const startTest = useCallback(async () => {
    abortRef.current = false;
    pauseRef.current = false;
    setPaused(false);
    resultsRef.current = {};

    thresholdsDoneRef.current = 0;

    const results = {
      Right: { 1000: 90, 2000: 90, 4000: 90, 6000: 90, 8000: 90, 500: 90 },
      Left: { 1000: 90, 2000: 90, 4000: 90, 6000: 90, 8000: 90, 500: 90 },
    };

    resultsRef.current = results;

    try {
      await Tone.start();
    } catch { }

    for (const ear of ["Left", "Right"]) {
      if (abortRef.current || developerRef.current) break;
      const pan = ear === "Left" ? -1 : 1;
      const freqs = [...FREQUENCIES_ORDER];

      for (let fi = 0; fi < freqs.length; fi++) {
        if (abortRef.current || developerRef.current) break;
        const freq = freqs[fi];
        const initialThreshold = await findInitialThreshold(freq, pan);
        if (abortRef.current || developerRef.current) break;
        if (initialThreshold === null) {
          await tick();
          continue;
        }
        if (initialThreshold === ABOVE_90_DBHL) {
          resultsRef.current[ear][freq] = ABOVE_90_DBHL;
        } else {
          const finalThreshold = await refineThreshold(freq, pan, initialThreshold);
          if (finalThreshold !== null) {
            resultsRef.current[ear][freq] = finalThreshold;
            thresholdsDoneRef.current += 1;
          }
        }
        await tick();
      }
    }

    // Without this wait, Cypress will sometimes be unable to locate the
    // test button with its data-listening attribute set to 'false' for
    // the final time before the page is navigated to results.
    if (isCypress) {
      await new Promise((r) => setTimeout(r, 1000));
    }

    if (!abortRef.current || developerRef.current) {
      try {
        sessionStorage.setItem('diagnosticResults', JSON.stringify(resultsRef.current));
        navigate("/results/diagnostic", {
          replace: true,
          state: { ...(location.state || {}), from: location.pathname }
        });
      } catch (e) { }
    }

    setStarted(false);
    setListening(false);
    setPaused(false);

    setNoiseModalOpen(false);
    setNoiseTryAgain(false);
  }, [tick, navigate, findInitialThreshold, refineThreshold, location]);

  const onBegin = useCallback(async () => {
    if (started) return;

    setStarted(true);
    startTest().catch(() => {
      setStarted(false);
      setListening(false);
      setPaused(false);
      setNoiseModalOpen(false);
      setNoiseTryAgain(false);
    });
  }, [startTest, started]);

  let content = null;

  if (spamDetected) {
    content = (
      <Box className="hearing-test-page hearing-test-page--centered">
        <Paper elevation={3} className="hearing-test-card--centered">
          <Typography variant="body2" className="hearing-test-warning-text">
            {t('hearing.spamWarning')}
          </Typography>

          <Button variant="contained" onClick={() => { dismissSpamWarning(); }}>
            {t('hearing.resume')}
          </Button>
        </Paper>
      </Box>
    );
  } else if (!started && !paused) {
    content = (
      <Box className="hearing-test-page">
        <Paper elevation={3} className="hearing-test-card hearing-test-welcome-card">
          <Typography component="h2" variant="h5" className="hearing-test-welcome-title">
            {t('hearing.welcomeTitle')}
          </Typography>
          <Typography variant="body1" className="hearing-test-welcome-body">
            {t('hearing.welcomeBody')}
          </Typography>
          <Button data-cy="begin_diagnostic_btn" variant="contained" color="primary" size="large" onClick={onBegin}>
            {t('hearing.beginTest')}
          </Button>
        </Paper>
      </Box>
    );
  } else if (paused) {
    content = (
      <Box className="hearing-test-page hearing-test-page--centered">
        <Paper elevation={3} className="hearing-test-card--centered">
          <Button variant="contained" onClick={() => resumeTest()}>
            {t('hearing.resume')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              stopTest();
              navigate("/testflow", {
                replace: true,
                state: {
                  ...(location.state || {}),
                  from: location.pathname,
                  step: "test",
                },
              });
            }}
          >
            {t('common.exit')}
          </Button>
        </Paper>
      </Box>
    );
  } else {
    content = (
      <Box className="hearing-test-page">
        <Paper elevation={3} className="hearing-test-card hearing-test-active-card">
          <Typography component="h2" variant="h6" align="center" className="hearing-test-instruction">
            {t('hearing.pressRedSquare')}
          </Typography>
          <Box className="hearing-test-button-wrap">
            <Box className="hearing-test-button-wrap">
              <ButtonBase
                data-cy="test_btn"
                data-listening={listening}
                onClick={handleRespond}
                className="hearing-test-response-button"
                focusRipple
                aria-label={t('hearing.heardToneAria')}
                disabled={noiseModalOpen}
              >
                <Paper elevation={6} className="hearing-test-response-square"/>
              </ButtonBase>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => pauseTest()}
            className="hearing-test-pause-btn"
            disabled={noiseModalOpen}
          >
            {t('hearing.pause')}
          </Button>
          {process.env.NODE_ENV === 'development' && <Button
            variant="contained"
            color="secondary"
            onClick={() => { developerRef.current = true }}
            className="hearing-test-developer-btn"
            disabled={noiseModalOpen}
          >
            Developer
          </Button>}
        </Paper>
      </Box>
    );
  }

  return (
    <>
      {content}

      <Dialog open={noiseModalOpen} maxWidth="xs" fullWidth>
        <DialogTitle>{t('hearing.tooLoudTitle')}</DialogTitle>
        <DialogContent>
          <Typography className="hearing-test-dialog-text">
            {t('hearing.waitQuiet')}
          </Typography>

          {typeof estimatedDb === "number" && (
            <Typography variant="body2" className="hearing-test-dialog-subtext">
              {t('hearing.noiseLevel', { db: estimatedDb.toFixed(0) })}
            </Typography>
          )}

          {noiseError && <Alert severity="error">{noiseError}</Alert>}

          {noiseTryAgain && !canResume && (
            <Alert severity="warning" className="hearing-test-dialog-alert">
              {t('hearing.tryAgain')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleNoiseResume} disabled={!canResume}>
            {t('hearing.resumeTest')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default HearingTest;